import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { TreePine, Plus, ArrowUpCircle, ArrowDownCircle, Package, TrendingUp, TrendingDown, History, RefreshCw, Trash2, Pencil, FileCheck, AlertTriangle, Database } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Função para formatar moeda brasileira
const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export default function EstoqueMadeira() {
  const [estoques, setEstoques] = useState([]);
  const [resumo, setResumo] = useState({ total_m3: 0, total_valor: 0, qtd_tipos: 0 });
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [tiposMadeira, setTiposMadeira] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Estados para DOF
  const [dofResumo, setDofResumo] = useState({ resumo: [], total_pendente_baixa: 0 });
  const [dofHistorico, setDofHistorico] = useState([]);
  const [dofDialogOpen, setDofDialogOpen] = useState(false);
  const [dofForm, setDofForm] = useState({
    tipo_madeira_id: '',
    volume_m3: '',
    observacao: ''
  });
  
  // Estados para Saldo DOF
  const [saldoDofDialogOpen, setSaldoDofDialogOpen] = useState(false);
  const [saldoDofForm, setSaldoDofForm] = useState({
    tipo_madeira_id: '',
    saldo_m3: '',
    observacao: ''
  });

  // Estado para ajuste de saldo do estoque físico
  const [ajusteSaldoDialogOpen, setAjusteSaldoDialogOpen] = useState(false);
  const [ajusteSaldoForm, setAjusteSaldoForm] = useState({
    tipo_madeira_id: '',
    tipo_madeira_nome: '',
    saldo_atual: 0,
    novo_saldo_m3: '',
    observacao: ''
  });
  
  const [entradaForm, setEntradaForm] = useState({
    tipo_madeira_id: '',
    volume_m3: '',
    valor_total: '',
    observacao: '',
    nota_fiscal: '',
    fornecedor: ''
  });

  // Estado para edição de movimentação
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMov, setEditingMov] = useState(null);
  const [editForm, setEditForm] = useState({
    volume_m3: '',
    valor_total: '',
    observacao: '',
    nota_fiscal: '',
    fornecedor: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [estoquesRes, resumoRes, movRes, tiposRes, dofResumoRes, dofHistRes] = await Promise.all([
        axios.get(`${API}/estoque-madeira`),
        axios.get(`${API}/estoque-madeira/resumo`),
        axios.get(`${API}/estoque-madeira/movimentacoes?limit=50`),
        axios.get(`${API}/tipos-madeira`),
        axios.get(`${API}/dof/resumo`),
        axios.get(`${API}/dof/historico?limit=50`)
      ]);
      setEstoques(estoquesRes.data);
      setResumo(resumoRes.data);
      setMovimentacoes(movRes.data);
      setTiposMadeira(tiposRes.data);
      setDofResumo(dofResumoRes.data);
      setDofHistorico(dofHistRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEntrada = async (e) => {
    e.preventDefault();
    if (!entradaForm.tipo_madeira_id) {
      toast.error('Selecione o tipo de madeira');
      return;
    }
    if (!entradaForm.volume_m3 || parseFloat(entradaForm.volume_m3) <= 0) {
      toast.error('Informe um volume válido');
      return;
    }
    if (!entradaForm.valor_total || parseFloat(entradaForm.valor_total) <= 0) {
      toast.error('Informe o valor total da compra');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/estoque-madeira/entrada`, {
        tipo_madeira_id: entradaForm.tipo_madeira_id,
        volume_m3: parseFloat(entradaForm.volume_m3),
        valor_total: parseFloat(entradaForm.valor_total),
        observacao: entradaForm.observacao,
        nota_fiscal: entradaForm.nota_fiscal,
        fornecedor: entradaForm.fornecedor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const custoM3 = response.data.custo_m3_entrada;
      const custoMedio = response.data.custo_medio_m3;
      toast.success(`Entrada registrada! Custo/m³: ${formatarMoeda(custoM3)} | Custo médio: ${formatarMoeda(custoMedio)}`);
      setDialogOpen(false);
      setEntradaForm({ tipo_madeira_id: '', volume_m3: '', valor_total: '', observacao: '', nota_fiscal: '', fornecedor: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar entrada');
    }
  };

  // Handler: Excluir movimentação
  const handleDeleteMovimentacao = async (movimentacaoId, tipoMov) => {
    if (!window.confirm(`Deseja excluir esta ${tipoMov === 'entrada' ? 'entrada' : 'saída'}? O saldo será revertido automaticamente.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/estoque-madeira/movimentacoes/${movimentacaoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Movimentação excluída e saldo revertido!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir movimentação');
    }
  };

  // Handler: Abrir modal de edição
  const handleOpenEdit = (mov) => {
    setEditingMov(mov);
    setEditForm({
      volume_m3: mov.volume_m3?.toString() || '',
      valor_total: mov.valor_total?.toString() || '',
      observacao: mov.observacao || '',
      nota_fiscal: mov.nota_fiscal || '',
      fornecedor: mov.fornecedor || ''
    });
    setEditDialogOpen(true);
  };

  // Handler: Salvar edição
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingMov) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/estoque-madeira/movimentacoes/${editingMov.id}`, {
        volume_m3: editForm.volume_m3 ? parseFloat(editForm.volume_m3) : undefined,
        valor_total: editForm.valor_total ? parseFloat(editForm.valor_total) : undefined,
        observacao: editForm.observacao,
        nota_fiscal: editForm.nota_fiscal,
        fornecedor: editForm.fornecedor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Movimentação atualizada com sucesso!');
      setEditDialogOpen(false);
      setEditingMov(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar movimentação');
    }
  };

  // Handler: Registrar baixa no DOF
  const handleBaixaDOF = async (e) => {
    e.preventDefault();
    if (!dofForm.tipo_madeira_id) {
      toast.error('Selecione o tipo de madeira');
      return;
    }
    if (!dofForm.volume_m3 || parseFloat(dofForm.volume_m3) <= 0) {
      toast.error('Informe um volume válido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/dof/baixa`, {
        tipo_madeira_id: dofForm.tipo_madeira_id,
        volume_m3: parseFloat(dofForm.volume_m3),
        observacao: dofForm.observacao
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Baixa registrada no DOF com sucesso!');
      setDofDialogOpen(false);
      setDofForm({ tipo_madeira_id: '', volume_m3: '', observacao: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar baixa no DOF');
    }
  };

  // Handler: Cadastrar saldo DOF
  const handleCadastrarSaldoDOF = async (e) => {
    e.preventDefault();
    if (!saldoDofForm.tipo_madeira_id) {
      toast.error('Selecione o tipo de madeira');
      return;
    }
    if (!saldoDofForm.saldo_m3 || parseFloat(saldoDofForm.saldo_m3) < 0) {
      toast.error('Informe um saldo válido');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/dof/saldo`, {
        tipo_madeira_id: saldoDofForm.tipo_madeira_id,
        saldo_m3: parseFloat(saldoDofForm.saldo_m3),
        observacao: saldoDofForm.observacao
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setSaldoDofDialogOpen(false);
      setSaldoDofForm({ tipo_madeira_id: '', saldo_m3: '', observacao: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao cadastrar saldo DOF');
    }
  };

  // Handler: Excluir baixa DOF
  const handleDeleteBaixaDOF = async (baixaId) => {
    if (!window.confirm('Deseja excluir esta baixa do DOF?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dof/baixa/${baixaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Baixa excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir baixa');
    }
  };

  // Handler: Ajustar saldo do estoque físico
  const handleAjustarSaldo = async (e) => {
    e.preventDefault();
    if (!ajusteSaldoForm.tipo_madeira_id) {
      toast.error('Tipo de madeira não selecionado');
      return;
    }
    if (ajusteSaldoForm.novo_saldo_m3 === '' || parseFloat(ajusteSaldoForm.novo_saldo_m3) < 0) {
      toast.error('Informe um saldo válido (≥ 0)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/estoque-madeira/ajustar-saldo`, {
        tipo_madeira_id: ajusteSaldoForm.tipo_madeira_id,
        novo_saldo_m3: parseFloat(ajusteSaldoForm.novo_saldo_m3),
        observacao: ajusteSaldoForm.observacao
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setAjusteSaldoDialogOpen(false);
      setAjusteSaldoForm({ tipo_madeira_id: '', tipo_madeira_nome: '', saldo_atual: 0, novo_saldo_m3: '', observacao: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao ajustar saldo');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TreePine className="h-7 w-7 text-amber-600" />
              Estoque de Madeira
            </h1>
            <p className="text-muted-foreground">Controle de entrada e saída de madeira em m³</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="nova-entrada-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Entrada
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <ArrowUpCircle className="h-5 w-5 text-green-500" />
                    Registrar Entrada de Madeira
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEntrada} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tipo de Madeira *</Label>
                    <Select 
                      value={entradaForm.tipo_madeira_id}
                      onValueChange={(v) => setEntradaForm({ ...entradaForm, tipo_madeira_id: v })}
                    >
                      <SelectTrigger data-testid="tipo-madeira-entrada-select">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposMadeira.map(tipo => (
                          <SelectItem key={tipo.id} value={tipo.id}>
                            {tipo.nome} - {formatarMoeda(tipo.preco_m3)}/m³
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Volume (m³) *</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      placeholder="0.0000"
                      value={entradaForm.volume_m3}
                      onChange={(e) => setEntradaForm({ ...entradaForm, volume_m3: e.target.value })}
                      data-testid="volume-entrada-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Valor Total da Compra (R$) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={entradaForm.valor_total}
                      onChange={(e) => setEntradaForm({ ...entradaForm, valor_total: e.target.value })}
                      data-testid="valor-total-entrada-input"
                    />
                    {entradaForm.volume_m3 && entradaForm.valor_total && (
                      <p className="text-xs text-muted-foreground">
                        Custo por m³: {formatarMoeda(parseFloat(entradaForm.valor_total) / parseFloat(entradaForm.volume_m3))}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nota Fiscal</Label>
                      <Input
                        placeholder="Número da NF"
                        value={entradaForm.nota_fiscal}
                        onChange={(e) => setEntradaForm({ ...entradaForm, nota_fiscal: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fornecedor</Label>
                      <Input
                        placeholder="Nome do fornecedor"
                        value={entradaForm.fornecedor}
                        onChange={(e) => setEntradaForm({ ...entradaForm, fornecedor: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Observação</Label>
                    <Input
                      placeholder="Observação opcional"
                      value={entradaForm.observacao}
                      onChange={(e) => setEntradaForm({ ...entradaForm, observacao: e.target.value })}
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    <ArrowUpCircle className="h-4 w-4 mr-2" />
                    Registrar Entrada
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={`${resumo.total_m3 < 0 ? 'border-red-500/50 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Volume Total em Estoque</p>
                  <p className={`text-3xl font-bold ${(resumo.total_m3 ?? 0) < 0 ? 'text-red-500' : ''}`}>
                    {(resumo.total_m3 ?? 0).toFixed(4)} m³
                  </p>
                  {(resumo.total_m3 ?? 0) < 0 && (
                    <p className="text-xs text-red-500 mt-1">⚠️ Estoque negativo</p>
                  )}
                </div>
                <Package className={`h-10 w-10 ${(resumo.total_m3 ?? 0) < 0 ? 'text-red-500' : 'text-amber-600'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className={`${resumo.total_valor < 0 ? 'border-red-500/50 bg-red-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valor do Estoque</p>
                  <p className={`text-3xl font-bold ${resumo.total_valor < 0 ? 'text-red-500' : ''}`}>
                    {formatarMoeda(resumo.total_valor)}
                  </p>
                </div>
                <TrendingUp className={`h-10 w-10 ${resumo.total_valor < 0 ? 'text-red-500' : 'text-green-600'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tipos com Estoque</p>
                  <p className="text-3xl font-bold">{resumo.qtd_tipos}</p>
                </div>
                <TreePine className="h-10 w-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className={`${dofResumo.total_pendente_baixa > 0 ? 'border-orange-500/50 bg-orange-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendente Baixa DOF</p>
                  <p className={`text-3xl font-bold ${dofResumo.total_pendente_baixa > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {dofResumo.total_pendente_baixa?.toFixed(4) || '0.0000'} m³
                  </p>
                </div>
                <FileCheck className={`h-10 w-10 ${dofResumo.total_pendente_baixa > 0 ? 'text-orange-600' : 'text-green-600'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="saldo" className="w-full">
          <TabsList>
            <TabsTrigger value="saldo" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Saldo por Tipo
            </TabsTrigger>
            <TabsTrigger value="movimentacoes" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Movimentações
            </TabsTrigger>
            <TabsTrigger value="dof" className="flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              Controle DOF
              {dofResumo.total_pendente_baixa > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">!</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="saldo" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {estoques.map(estoque => {
                const saldoM3 = estoque.saldo_m3 ?? 0;
                const isNegativo = saldoM3 < 0;
                return (
                <Card 
                  key={estoque.tipo_madeira_id} 
                  className={`hover:border-primary/50 transition-colors ${isNegativo ? 'border-red-500/50 bg-red-500/5' : ''}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <TreePine className={`h-5 w-5 ${isNegativo ? 'text-red-500' : 'text-amber-600'}`} />
                        {estoque.tipo_madeira_nome}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant={isNegativo ? "destructive" : saldoM3 > 0 ? "default" : "secondary"}>
                          {isNegativo ? 'Estoque Negativo' : saldoM3 > 0 ? 'Em estoque' : 'Sem estoque'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            setAjusteSaldoForm({
                              tipo_madeira_id: estoque.tipo_madeira_id,
                              tipo_madeira_nome: estoque.tipo_madeira_nome,
                              saldo_atual: estoque.saldo_m3 ?? 0,
                              novo_saldo_m3: (estoque.saldo_m3 ?? 0).toString(),
                              observacao: ''
                            });
                            setAjusteSaldoDialogOpen(true);
                          }}
                          title="Ajustar saldo"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardTitle>
                    <CardDescription>
                      Preço referência: {formatarMoeda(estoque.preco_m3)}/m³
                      {estoque.custo_medio_m3 > 0 && (
                        <span className="ml-2 text-blue-500">
                          | Custo médio: {formatarMoeda(estoque.custo_medio_m3)}/m³
                        </span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-sm text-muted-foreground">Volume</p>
                        <p className={`text-2xl font-bold ${isNegativo ? 'text-red-500' : ''}`}>
                          {(estoque.saldo_m3 ?? 0).toFixed(4)} m³
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Valor (custo médio)</p>
                        <p className={`text-lg font-semibold ${isNegativo ? 'text-red-500' : 'text-green-600'}`}>
                          {formatarMoeda(estoque.valor_total_estoque || estoque.valor_estoque)}
                        </p>
                      </div>
                    </div>
                    {isNegativo && (
                      <p className="text-xs text-red-500 mt-2 font-medium">
                        ⚠️ Necessário repor {Math.abs(estoque.saldo_m3 ?? 0).toFixed(4)} m³
                      </p>
                    )}
                  </CardContent>
                </Card>
              )})}

              {estoques.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="pt-6 text-center text-muted-foreground">
                    <TreePine className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum tipo de madeira cadastrado.</p>
                    <p className="text-sm">Cadastre tipos de madeira em Configurações → Madeiras</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Modal de Ajuste de Saldo */}
            <Dialog open={ajusteSaldoDialogOpen} onOpenChange={setAjusteSaldoDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-blue-500" />
                    Ajustar Saldo - {ajusteSaldoForm.tipo_madeira_nome}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAjustarSaldo} className="space-y-4">
                  <div className="p-3 bg-amber-500/10 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                    Este ajuste cria uma movimentação de entrada ou saída automática para corrigir o saldo.
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm text-muted-foreground">Saldo Atual</p>
                      <p className="text-xl font-bold">{ajusteSaldoForm.saldo_atual?.toFixed(4)} m³</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Novo Saldo (m³) *</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        placeholder="0.0000"
                        value={ajusteSaldoForm.novo_saldo_m3}
                        onChange={(e) => setAjusteSaldoForm({ ...ajusteSaldoForm, novo_saldo_m3: e.target.value })}
                        className="text-lg"
                      />
                    </div>
                  </div>

                  {ajusteSaldoForm.novo_saldo_m3 !== '' && (
                    <div className={`p-3 rounded-lg text-sm ${
                      parseFloat(ajusteSaldoForm.novo_saldo_m3) > ajusteSaldoForm.saldo_atual 
                        ? 'bg-green-500/10 text-green-700' 
                        : parseFloat(ajusteSaldoForm.novo_saldo_m3) < ajusteSaldoForm.saldo_atual
                        ? 'bg-red-500/10 text-red-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {parseFloat(ajusteSaldoForm.novo_saldo_m3) > (ajusteSaldoForm.saldo_atual ?? 0) 
                        ? `↑ Entrada de +${(parseFloat(ajusteSaldoForm.novo_saldo_m3) - (ajusteSaldoForm.saldo_atual ?? 0)).toFixed(4)} m³`
                        : parseFloat(ajusteSaldoForm.novo_saldo_m3) < (ajusteSaldoForm.saldo_atual ?? 0)
                        ? `↓ Saída de -${((ajusteSaldoForm.saldo_atual ?? 0) - parseFloat(ajusteSaldoForm.novo_saldo_m3)).toFixed(4)} m³`
                        : '→ Sem alteração'
                      }
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Observação</Label>
                    <Input
                      placeholder="Ex: Ajuste após inventário físico"
                      value={ajusteSaldoForm.observacao}
                      onChange={(e) => setAjusteSaldoForm({ ...ajusteSaldoForm, observacao: e.target.value })}
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAjusteSaldoDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Ajustar Saldo
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="movimentacoes" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Movimentações</CardTitle>
                <CardDescription>Últimas 50 movimentações de entrada e saída</CardDescription>
              </CardHeader>
              <CardContent>
                {movimentacoes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma movimentação registrada ainda.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {movimentacoes.map(mov => (
                      <div 
                        key={mov.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          mov.tipo === 'entrada' 
                            ? 'bg-green-500/5 border-green-500/30' 
                            : 'bg-red-500/5 border-red-500/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {mov.tipo === 'entrada' ? (
                            <ArrowUpCircle className="h-6 w-6 text-green-500" />
                          ) : (
                            <ArrowDownCircle className="h-6 w-6 text-red-500" />
                          )}
                          <div>
                            <p className="font-medium">
                              {mov.tipo === 'entrada' ? 'Entrada' : 'Saída'} - {mov.tipo_madeira_nome}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {mov.observacao || (mov.pedido_numero ? `Pedido ${mov.pedido_numero}` : '-')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(mov.createdAt).toLocaleString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className={`text-lg font-bold ${mov.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                              {mov.tipo === 'entrada' ? '+' : '-'}{(mov.volume_m3 ?? 0).toFixed(4)} m³
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Saldo: {(mov.saldo_posterior ?? 0).toFixed(4)} m³
                            </p>
                            {mov.custo_m3 && (
                              <p className="text-xs text-blue-500">
                                Custo: {formatarMoeda(mov.custo_m3)}/m³
                              </p>
                            )}
                          </div>
                          {/* Botões de ação - não mostrar para saídas de pedidos */}
                          {!mov.pedido_id && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-500 hover:text-blue-600"
                                onClick={() => handleOpenEdit(mov)}
                                title="Editar movimentação"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteMovimentacao(mov.id, mov.tipo)}
                                title="Excluir movimentação"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba de Controle DOF */}
          <TabsContent value="dof" className="mt-4 space-y-4">
            {/* Botões para registrar baixa e cadastrar saldo */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-lg font-semibold">Controle DOF/IBAMA</h2>
                <p className="text-sm text-muted-foreground">Registre as baixas no DOF e acompanhe o consumo</p>
              </div>
              <div className="flex gap-2">
                {/* Botão Cadastrar Saldo DOF */}
                <Dialog open={saldoDofDialogOpen} onOpenChange={setSaldoDofDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="cadastrar-saldo-dof-btn">
                      <Database className="h-4 w-4 mr-2" />
                      Cadastrar Saldo DOF
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5 text-blue-500" />
                        Cadastrar Saldo no DOF
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCadastrarSaldoDOF} className="space-y-4">
                      <div className="p-3 bg-blue-500/10 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                        Informe o saldo atual disponível no DOF do IBAMA para este tipo de madeira.
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Tipo de Madeira *</Label>
                        <Select 
                          value={saldoDofForm.tipo_madeira_id}
                          onValueChange={(v) => setSaldoDofForm({ ...saldoDofForm, tipo_madeira_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposMadeira.map(tipo => (
                              <SelectItem key={tipo.id} value={tipo.id}>
                                {tipo.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Saldo Disponível no DOF (m³) *</Label>
                        <Input
                          type="number"
                          step="0.0001"
                          placeholder="0.0000"
                          value={saldoDofForm.saldo_m3}
                          onChange={(e) => setSaldoDofForm({ ...saldoDofForm, saldo_m3: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Observação</Label>
                        <Input
                          placeholder="Ex: Saldo atualizado conforme DOF nº 12345"
                          value={saldoDofForm.observacao}
                          onChange={(e) => setSaldoDofForm({ ...saldoDofForm, observacao: e.target.value })}
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setSaldoDofDialogOpen(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                          <Database className="h-4 w-4 mr-2" />
                          Salvar Saldo
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Botão Registrar Baixa DOF */}
                <Dialog open={dofDialogOpen} onOpenChange={setDofDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="nova-baixa-dof-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Registrar Baixa DOF
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FileCheck className="h-5 w-5 text-green-500" />
                        Registrar Baixa no DOF
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleBaixaDOF} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Tipo de Madeira *</Label>
                        <Select 
                          value={dofForm.tipo_madeira_id}
                          onValueChange={(v) => setDofForm({ ...dofForm, tipo_madeira_id: v })}
                        >
                          <SelectTrigger data-testid="tipo-madeira-dof-select">
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposMadeira.map(tipo => (
                              <SelectItem key={tipo.id} value={tipo.id}>
                                {tipo.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                      <Label>Volume da Baixa (m³) *</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        placeholder="0.0000"
                        value={dofForm.volume_m3}
                        onChange={(e) => setDofForm({ ...dofForm, volume_m3: e.target.value })}
                        data-testid="volume-dof-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Observação</Label>
                      <Input
                        placeholder="Ex: Baixa referente à produção de Abril"
                        value={dofForm.observacao}
                        onChange={(e) => setDofForm({ ...dofForm, observacao: e.target.value })}
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setDofDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="bg-green-600 hover:bg-green-700">
                        <FileCheck className="h-4 w-4 mr-2" />
                        Registrar Baixa
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {/* Resumo DOF por tipo de madeira */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  Situação DOF por Tipo de Madeira
                </CardTitle>
                <CardDescription>
                  Estoque físico vs. controle DOF - consumo após última baixa
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dofResumo.resumo?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhum tipo de madeira com movimentação registrada.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {dofResumo.resumo?.map(item => (
                      <div 
                        key={item.tipo_madeira_id}
                        className={`p-4 rounded-lg border ${
                          item.pendente_baixa_dof > 0.5 
                            ? 'bg-orange-500/5 border-orange-500/30' 
                            : 'bg-green-500/5 border-green-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <TreePine className="h-5 w-5 text-amber-600" />
                            <span className="font-semibold">{item.tipo_madeira_nome}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {item.saldo_cadastrado_dof > 0 && (
                              <Badge variant="outline" className="border-blue-500 text-blue-600">
                                <Database className="h-3 w-3 mr-1" />
                                DOF: {item.saldo_cadastrado_dof?.toFixed(2)} m³
                              </Badge>
                            )}
                            {item.pendente_baixa_dof > 0.01 && (
                              <Badge variant="outline" className="border-orange-500 text-orange-600">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Pendente Baixa
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => {
                                setSaldoDofForm({
                                  tipo_madeira_id: item.tipo_madeira_id,
                                  saldo_m3: item.saldo_cadastrado_dof?.toString() || '',
                                  observacao: ''
                                });
                                setSaldoDofDialogOpen(true);
                              }}
                              title="Editar saldo DOF"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Estoque Físico</p>
                            <p className="font-medium">{item.saldo_fisico?.toFixed(4)} m³</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Saldo Cadastrado DOF</p>
                            <p className="font-medium text-blue-600">{item.saldo_cadastrado_dof?.toFixed(4) || '0.0000'} m³</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Total Baixado</p>
                            <p className="font-medium text-green-600">{item.total_baixado_dof?.toFixed(4)} m³</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Saldo Disponível DOF</p>
                            <p className={`font-medium ${(item.saldo_disponivel_dof || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {item.saldo_disponivel_dof?.toFixed(4) || '0.0000'} m³
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Pendente de Baixa</p>
                            <p className={`font-bold ${item.pendente_baixa_dof > 0.01 ? 'text-orange-600' : 'text-green-600'}`}>
                              {item.pendente_baixa_dof?.toFixed(4)} m³
                            </p>
                          </div>
                        </div>
                        {item.ultima_baixa && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Última baixa: {new Date(item.ultima_baixa).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Histórico de baixas */}
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Baixas DOF</CardTitle>
                <CardDescription>Últimas 50 baixas registradas</CardDescription>
              </CardHeader>
              <CardContent>
                {dofHistorico.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma baixa DOF registrada ainda.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {dofHistorico.map(baixa => (
                      <div 
                        key={baixa.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-green-500/5 border-green-500/30"
                      >
                        <div className="flex items-center gap-3">
                          <FileCheck className="h-6 w-6 text-green-500" />
                          <div>
                            <p className="font-medium">
                              Baixa DOF - {baixa.tipo_madeira_nome}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {baixa.observacao || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(baixa.createdAt).toLocaleString('pt-BR')} - por {baixa.registrado_por}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              {(baixa.volume_m3 ?? 0).toFixed(4)} m³
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteBaixaDOF(baixa.id)}
                            title="Excluir baixa"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modal de Edição de Movimentação */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-500" />
                Editar Movimentação
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Volume (m³)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="0.0000"
                  value={editForm.volume_m3}
                  onChange={(e) => setEditForm({ ...editForm, volume_m3: e.target.value })}
                />
              </div>

              {editingMov?.tipo === 'entrada' && (
                <div className="space-y-2">
                  <Label>Valor Total da Compra (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editForm.valor_total}
                    onChange={(e) => setEditForm({ ...editForm, valor_total: e.target.value })}
                  />
                  {editForm.volume_m3 && editForm.valor_total && (
                    <p className="text-xs text-muted-foreground">
                      Custo por m³: {formatarMoeda(parseFloat(editForm.valor_total) / parseFloat(editForm.volume_m3))}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nota Fiscal</Label>
                  <Input
                    placeholder="Número da NF"
                    value={editForm.nota_fiscal}
                    onChange={(e) => setEditForm({ ...editForm, nota_fiscal: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fornecedor</Label>
                  <Input
                    placeholder="Nome do fornecedor"
                    value={editForm.fornecedor}
                    onChange={(e) => setEditForm({ ...editForm, fornecedor: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  placeholder="Observação opcional"
                  value={editForm.observacao}
                  onChange={(e) => setEditForm({ ...editForm, observacao: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full">
                <Pencil className="h-4 w-4 mr-2" />
                Salvar Alterações
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
